/**
 * «Surtido» — names beside codes: a store and an article are shown by NAME with the code beside it
 * («Bogotá Centro (5262)»), on every screen and in every file (ADR 9070 R3, ADR 9058).
 *
 * **Where the names come from.** The plan itself, by key: the platform names any asked key with the rule
 * the in-season run's names use (the store master in force, the attribute row in force on the plan's day),
 * at most one hundred of each kind per request. A name is a property of its key within the plan, so one
 * dictionary per plan serves every row of every screen of it.
 *
 * **Only the keys a screen draws.** A lookup the dictionary cannot answer registers the key; the keys of one
 * render are asked in one request (more go in more). Until it answers, a key reads as its code («Tienda 5262»):
 * nothing waits on the names, and a key the platform cannot name keeps the code. Never a guess.
 *
 * The in-season hook that does the same (`inseason/run-names.tsx`) is not importable across features (G11), so this
 * is the pre-season one, over the plan's read. The files read the same names in the same chunks (`fetchNames`).
 */

import { createContext, useContext, useMemo, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import { callOperation } from '@/api';
import { readPlanNames } from '@/api/generated';

/** The most keys of one kind the platform names in one request. */
export const NAMES_CAP = 100;

export type NameKind = 'store' | 'article';

/** What a read of names answered, by key; `null` is a key the platform could not name. */
export interface NameBook {
  readonly stores: ReadonlyMap<string, string | null>;
  readonly articles: ReadonlyMap<string, string | null>;
}

/** Ask the plan to name `storeKeys` and `articleKeys`, in chunks of the cap, and add the answers to `into`. */
export async function fetchNames(
  planId: string, storeKeys: readonly string[], articleKeys: readonly string[],
  into: { readonly stores: Map<string, string | null>; readonly articles: Map<string, string | null> },
): Promise<void> {
  const chunks = (keys: readonly string[]): readonly (readonly string[])[] => {
    const out: string[][] = [];
    for (let at = 0; at < keys.length; at += NAMES_CAP) out.push(keys.slice(at, at + NAMES_CAP));
    return out;
  };
  const asks: { readonly stores: readonly string[]; readonly articles: readonly string[] }[] = [];
  const storeChunks = chunks(storeKeys);
  const articleChunks = chunks(articleKeys);
  for (let i = 0; i < Math.max(storeChunks.length, articleChunks.length); i += 1) {
    asks.push({ stores: storeChunks[i] ?? [], articles: articleChunks[i] ?? [] });
  }
  for (const ask of asks) {
    try {
      const answer = await callOperation(readPlanNames, { path: { plan_id: planId }, query: { store_key: [...ask.stores], article_key: [...ask.articles] } });
      for (const one of answer.stores ?? []) into.stores.set(one.store_key, one.store_name ?? null);
      for (const one of answer.articles ?? []) into.articles.set(one.article_key, one.article_name ?? null);
    } catch {
      // A read that failed names nothing: the keys keep their codes, and are not asked again.
    }
    for (const key of ask.stores) if (!into.stores.has(key)) into.stores.set(key, null);
    for (const key of ask.articles) if (!into.articles.has(key)) into.articles.set(key, null);
  }
}

/** The dictionary of one plan: answers what it knows, registers what it is asked and does not, and tells its readers when it learns. */
export class NamesStore {
  private readonly book = { stores: new Map<string, string | null>(), articles: new Map<string, string | null>() };
  private readonly asked = { stores: new Set<string>(), articles: new Set<string>() };
  private readonly listeners = new Set<() => void>();
  private version = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly planId: string) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  snapshot = (): number => this.version;

  /** The name of a key; `null` when none is known (yet), registering the key to be asked. */
  nameOf(kind: NameKind, key: string): string | null {
    const known = (kind === 'store' ? this.book.stores : this.book.articles).get(key);
    if (known !== undefined) return known;
    const wanted = kind === 'store' ? this.asked.stores : this.asked.articles;
    if (!wanted.has(key)) {
      wanted.add(key);
      this.schedule();
    }
    return null;
  }

  private schedule(): void {
    if (this.timer !== null) return;
    this.timer = setTimeout(() => { this.timer = null; void this.flush(); }, 0);
  }

  private async flush(): Promise<void> {
    const stores = [...this.asked.stores].filter((key) => !this.book.stores.has(key));
    const articles = [...this.asked.articles].filter((key) => !this.book.articles.has(key));
    if (stores.length === 0 && articles.length === 0) return;
    await fetchNames(this.planId, stores, articles, this.book);
    this.version += 1;
    for (const listener of this.listeners) listener();
  }
}

const NamesContext = createContext<NamesStore | null>(null);

/** The plan's names for everything under it. Keyed by the plan, so another plan starts a new dictionary. */
export function PlanNamesProvider({ planId, children }: { readonly planId: string; readonly children: React.ReactNode }): React.JSX.Element {
  const store = useMemo(() => new NamesStore(planId), [planId]);
  return <NamesContext.Provider value={store}>{children}</NamesContext.Provider>;
}

const NO_STORE = new NamesStore('');

function useStore(kind: NameKind): (key: string) => string {
  const { t } = useTranslation();
  const store = useContext(NamesContext) ?? NO_STORE;
  useSyncExternalStore(store.subscribe, store.snapshot);
  return (key) => {
    const name = store.nameOf(kind, key);
    if (name !== null && name !== '') return t('preseason.assortment.nameWithCode', { name, code: key });
    return kind === 'store' ? t('preseason.assortment.storeName', { store: key }) : t('preseason.assortment.articleName', { article: key });
  };
}

/** A store as the page names it: its name with its code beside it, or «Tienda 5262» while no name is known. */
export function useStoreName(): (storeKey: string) => string {
  return useStore('store');
}

/** An article as the page names it: its name with its code beside it, or «Artículo 1016917X» while no name is known. */
export function useArticleName(): (articleKey: string) => string {
  return useStore('article');
}

/** A store's bare name (no code), or `null` while none is known — for a title that carries the code apart. */
export function useBareStoreName(): (storeKey: string) => string | null {
  const store = useContext(NamesContext) ?? NO_STORE;
  useSyncExternalStore(store.subscribe, store.snapshot);
  return (key) => {
    const name = store.nameOf('store', key);
    return name === null || name === '' ? null : name;
  };
}
